import type { Character } from "../types/entity.js";
import type { DamageType } from "../types/combat.js";
import type { TrapMetadata } from "../types/tiled.js";
import type { CheckResult } from "../combat/skillCheck.js";
import { rollSkillCheck } from "../combat/skillCheck.js";
import { parseDiceNotation, rollChance, rollDice } from "../combat/dice.js";

/**
 * Trap runtime (W11).
 *
 * Pure resolver for Tiled `trap` objects. The runtime calls
 * `triggerTrap` when the party enters the trap cell (step_on), enters
 * an adjacent cell (proximity), or fails a search (search_fail). We
 * roll the authored damage dice, roll the optional condition chance,
 * and report what should happen — the caller is responsible for
 * calling `applyDamage` on each target and attaching conditions.
 *
 * Disarm is a separate entry point: the Rogue spends an action, we
 * run a DEX (Sleight of Hand) check against the authored DC, and the
 * caller decides whether the trap is removed on success or fires on
 * failure.
 *
 * Aside from the two RNG-backed dice rolls, the module is pure.
 */

/** How the trap was triggered. Mirrors `specs/world-and-progression.md` §Traps. */
export type TrapTrigger = "step_on" | "proximity" | "search_fail";

/** Optional RNG overrides for deterministic tests. */
export interface TrapRng {
  /** d6/d8/dN roller — defaults to `rollDice` from dice.ts. */
  readonly rollDice?: (count: number, sides: number) => number[];
  /**
   * Integer RNG in the `randomInt(min, max)` shape. Defaults to the
   * seeded engine RNG. Drives the condition-chance roll via the shared
   * `rollChance` helper so tests can seed it consistently with other
   * probability gates in the engine (loot tables, enemy AI).
   */
  readonly randomInt?: (min: number, max: number) => number;
}

export interface TrapTriggerInput {
  readonly meta: TrapMetadata;
  readonly trigger: TrapTrigger;
  /** Front-row (or AoE) party target instance IDs; the caller picks the set. */
  readonly targets: readonly string[];
  readonly rng?: TrapRng;
}

export interface TrapHit {
  readonly targetInstanceId: string;
  readonly damage: number;
  readonly damageType: DamageType;
  readonly conditionId: string | null;
}

export interface TrapTriggerResult {
  /** Damage dice rolled once; every target takes the same amount. */
  readonly damage: number;
  readonly damageType: DamageType;
  /** Condition rolled once for the whole trap (matches spec: single chance roll). */
  readonly conditionId: string | null;
  readonly hits: readonly TrapHit[];
}

export interface TrapDisarmInput {
  readonly rogue: Character;
  readonly meta: TrapMetadata;
}

export interface TrapDisarmResult {
  readonly check: CheckResult;
  /** Convenience: mirrors `check.success`. */
  readonly disarmed: boolean;
}

/**
 * Parse an `NdM[+K|-K]` dice expression. Thin wrapper over
 * `parseDiceNotation` in `combat/dice.ts` — kept here as a public export
 * so author-facing trap tests and UI can read "parse trap dice" without
 * knowing about the combat module.
 */
export { parseDiceNotation as parseDiceExpression } from "../combat/dice.js";

/**
 * Fire a trap. Returns per-target hit entries plus the single damage/condition
 * roll shared by every target in the burst (spec: one trap, one dice roll —
 * not one roll per victim).
 */
export function triggerTrap(input: TrapTriggerInput): TrapTriggerResult {
  const { meta, targets, rng } = input;

  const { count, sides, modifier } = parseDiceNotation(meta.damage_dice);
  const dice = (rng?.rollDice ?? rollDice)(count, sides);
  const damage = Math.max(0, dice.reduce((a, b) => a + b, 0) + modifier);
  const damageType = meta.damage_type as DamageType;

  let conditionId: string | null = null;
  if (meta.condition_id) {
    const chance = meta.condition_chance ?? 1;
    if (rollChance(chance, rng?.randomInt)) conditionId = meta.condition_id;
  }

  const hits: TrapHit[] = targets.map((targetInstanceId) => ({
    targetInstanceId,
    damage,
    damageType,
    conditionId,
  }));

  return { damage, damageType, conditionId, hits };
}

/**
 * Attempt to disarm a trap. Spec calls out Rogue DEX check; Sleight of
 * Hand is the DEX skill used for fine manipulation in 5e, so proficiency
 * and expertise flow through naturally for rogues.
 */
export function attemptDisarm(input: TrapDisarmInput): TrapDisarmResult {
  const check = rollSkillCheck(input.rogue, "Sleight of Hand", input.meta.disarm_dc);
  return { check, disarmed: check.success };
}

/** What a disarm attempt does to the world (W11c). */
export type DisarmOutcome = "disarmed" | "triggered" | "retry";

/**
 * Map a disarm check onto its table consequence (W11c, resolving the
 * spec's open "disarm UX" question): success disarms; a near miss just
 * resists (retry allowed, no cost beyond the risk); failing by 5 or more
 * sets the trap off in the disarmer's hands.
 */
export const DISARM_FUMBLE_MARGIN = 5;

export function disarmOutcome(result: TrapDisarmResult, dc: number): DisarmOutcome {
  if (result.disarmed) return "disarmed";
  if (result.check.total <= dc - DISARM_FUMBLE_MARGIN) return "triggered";
  return "retry";
}
